import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Link2, Unlink, QrCode, Copy, ArrowUpDown, ArrowUp, ArrowDown, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { tippingBase } from "@/lib/domains";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

export default function AdminCards() {
  const [cards, setCards] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [qty, setQty] = useState(5);
  const [type, setType] = useState("qr");
  const [linkFor, setLinkFor] = useState(null);
  const [workerId, setWorkerId] = useState("");
  const [sortKey, setSortKey] = useState("code");
  const [sortDir, setSortDir] = useState("asc");
  const [exporting, setExporting] = useState("");
  const [stpFor, setStpFor] = useState(null);
  const [stpData, setStpData] = useState(null);
  const [stpLoading, setStpLoading] = useState(false);

  const load = () => {
    api.get("/admin/cards").then((r) => setCards(r.data)).catch(() => {});
    api.get("/admin/workers").then((r) => setWorkers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const { data } = await api.post("/admin/cards", { quantity: Number(qty), type });
      toast.success(`${data.count} card(s) created`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const doLink = async () => {
    try {
      await api.post("/admin/cards/link", { card_id: linkFor.id, worker_id: workerId });
      toast.success("Card linked");
      setLinkFor(null); setWorkerId("");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const unlink = async (id) => {
    try { await api.post(`/admin/cards/${id}/unlink`); toast.success("Card unlinked"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const openStp = async (card) => {
    setStpFor(card); setStpData(null); setStpLoading(true);
    try {
      const { data } = await api.post(`/admin/cards/${card.id}/scantopay-code`);
      setStpData(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setStpLoading(false); }
  };

  const printStp = () => {
    if (!stpData?.qr_url) return;
    const name = stpFor?.status === "linked" ? workerName(stpFor.worker_id) : "";
    const w = window.open("", "_blank", "width=420,height=620");
    if (!w) { toast.error("Allow pop-ups to print the lanyard."); return; }
    w.document.write(`<!doctype html><html><head><title>Scan to Pay — ${name}</title>
      <style>body{font-family:system-ui,Arial,sans-serif;text-align:center;margin:0;padding:28px;color:#101F38}
      h1{font-size:20px;margin:0 0 2px}p{margin:4px 0;color:#475569;font-size:13px}
      img{width:280px;height:280px;margin:16px auto;display:block}
      .code{font-family:monospace;font-size:15px;letter-spacing:1px;margin-top:6px}
      .tag{margin-top:14px;font-size:12px;color:#64748b}</style></head>
      <body onload="window.print()">
      <h1>Tip ${name || "me"}</h1>
      <p>Scan with any South African banking app</p>
      <img src="${stpData.qr_url}" alt="Scan to Pay QR" />
      <div class="code">${stpData.code}</div>
      <div class="tag">Powered by TiplyMe · Scan to Pay</div>
      </body></html>`);
    w.document.close();
  };

  const workerName = (id) => workers.find((w) => w.id === id)?.name || "—";

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const val = (c, key) => {
    if (key === "worker") return c.status === "linked" ? workerName(c.worker_id) : "";
    return (c[key] || "").toString();
  };
  const sorted = [...cards].sort((a, b) => {
    const r = val(a, sortKey).toLowerCase().localeCompare(val(b, sortKey).toLowerCase());
    return sortDir === "asc" ? r : -r;
  });

  const download = async (format) => {
    setExporting(format);
    try {
      const res = await api.get(`/admin/cards/export?format=${format}&sort=${sortKey}&order=${sortDir}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tiplyme-qr-cards-${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed. Please try again."); }
    finally { setExporting(""); }
  };

  const SortHead = ({ label, k, className = "" }) => (
    <TableHead className={className}>
      <button onClick={() => toggleSort(k)} data-testid={`sort-${k}`} className="inline-flex items-center gap-1 hover:text-foreground">
        {label}
        {sortKey === k ? (sortDir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />}
      </button>
    </TableHead>
  );

  return (
    <div className="animate-fade-up" data-testid="admin-cards">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">QR / NFC Cards</h1>
          <p className="mt-1 text-muted-foreground">Generate pre-printed codes and link them to workers.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => download("csv")} disabled={!!exporting} data-testid="export-cards-csv" className="gap-1.5 rounded-full">
            {exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Excel
          </Button>
          <Button variant="outline" onClick={() => download("pdf")} disabled={!!exporting} data-testid="export-cards-pdf" className="gap-1.5 rounded-full">
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} QR PDF
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button data-testid="create-cards-button" className="rounded-full bg-brand-green font-semibold text-white hover:bg-brand-green/90"><Plus className="mr-1 h-4 w-4" /> Generate cards</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Generate pre-printed cards</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Quantity</Label><Input type="number" min="1" max="100" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="card-qty-input" className="mt-1" /></div>
                <div>
                  <Label>Type</Label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger className="mt-1" data-testid="card-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="qr">QR code</SelectItem><SelectItem value="nfc">NFC tag</SelectItem></SelectContent>
                  </Select>
                </div>
                <DialogFooter><Button onClick={create} data-testid="card-generate-submit" className="rounded-full bg-brand-green font-semibold text-white hover:bg-brand-green/90">Generate</Button></DialogFooter>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="mt-5 overflow-x-auto rounded-2xl border border-border/60 bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <SortHead label="Code" k="code" /><SortHead label="Type" k="type" /><SortHead label="Status" k="status" /><SortHead label="Linked worker" k="worker" /><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((c) => (
              <TableRow key={c.id} data-testid="card-row">
                <TableCell className="font-mono font-semibold">{c.code}</TableCell>
                <TableCell><Badge variant="secondary" className="uppercase">{c.type}</Badge></TableCell>
                <TableCell>
                  <Badge className={`border-0 ${c.status === "linked" ? "bg-brand-green/15 text-brand-greenLight" : "bg-secondary text-muted-foreground"}`}>{c.status}</Badge>
                </TableCell>
                <TableCell className="text-sm">{c.status === "linked" ? workerName(c.worker_id) : "—"}</TableCell>
                <TableCell className="text-right">
                  {c.status === "linked" ? (
                    <div className="flex justify-end gap-2">
                      <button onClick={() => { navigator.clipboard.writeText(`${tippingBase()}/pay/${c.code}`); toast.success("Pay link copied"); }} data-testid={`copy-link-${c.code}`} className="rounded-lg border border-border p-2 hover:bg-secondary"><Copy className="h-3.5 w-3.5" /></button>
                      <button onClick={() => openStp(c)} data-testid={`stp-qr-${c.code}`} title="Scan to Pay QR" className="rounded-lg border border-border p-2 hover:bg-secondary"><QrCode className="h-3.5 w-3.5" /></button>
                      <Button size="sm" variant="outline" onClick={() => unlink(c.id)} data-testid={`unlink-${c.code}`} className="gap-1 rounded-lg"><Unlink className="h-3.5 w-3.5" /> Unlink</Button>
                    </div>
                  ) : (
                    <Button size="sm" onClick={() => setLinkFor(c)} data-testid={`link-${c.code}`} className="gap-1 rounded-lg bg-brand-green text-white hover:bg-brand-green/90"><Link2 className="h-3.5 w-3.5" /> Link</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {cards.length === 0 && <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground"><QrCode className="mx-auto mb-2 h-6 w-6" />No cards yet. Generate a batch.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!stpFor} onOpenChange={(o) => { if (!o) { setStpFor(null); setStpData(null); } }}>
        <DialogContent data-testid="stp-qr-dialog">
          <DialogHeader><DialogTitle className="font-heading">Scan to Pay QR — {stpFor?.code}</DialogTitle></DialogHeader>
          <div className="space-y-4 text-center">
            {stpLoading && <div className="py-10"><Loader2 className="mx-auto h-8 w-8 animate-spin text-brand-green" /><p className="mt-3 text-sm text-muted-foreground">Generating the worker's QR…</p></div>}
            {!stpLoading && stpData?.qr_url && (
              <>
                <p className="text-sm text-muted-foreground">Tippers scan this with any South African banking app — the tip lands in {stpFor?.status === "linked" ? workerName(stpFor.worker_id) : "the worker"}'s wallet.</p>
                <img src={stpData.qr_url} alt="Scan to Pay QR" className="mx-auto h-56 w-56 rounded-xl border border-border bg-white p-2" data-testid="stp-qr-image" />
                <p className="font-mono text-sm tracking-wide" data-testid="stp-qr-code">{stpData.code}</p>
                <Button onClick={printStp} data-testid="print-stp-lanyard" className="w-full gap-2 rounded-full bg-brand-green font-semibold text-white hover:bg-brand-green/90"><FileText className="h-4 w-4" /> Print lanyard</Button>
              </>
            )}
            {!stpLoading && !stpData?.qr_url && (
              <p className="py-8 text-sm text-muted-foreground">Couldn't generate the QR. Make sure Scan to Pay is configured in Admin → Finance.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!linkFor} onOpenChange={(o) => !o && setLinkFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Link {linkFor?.code}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Select worker</Label>
            <Select value={workerId} onValueChange={setWorkerId}>
              <SelectTrigger data-testid="link-worker-select"><SelectValue placeholder="Choose a worker" /></SelectTrigger>
              <SelectContent>{workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name} — {w.workplace_name || "no workplace"}</SelectItem>)}</SelectContent>
            </Select>
            <DialogFooter><Button onClick={doLink} disabled={!workerId} data-testid="confirm-link-button" className="rounded-full bg-brand-green font-semibold text-white hover:bg-brand-green/90">Link card</Button></DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
